/**
 * Hit testing and marquee selection for plan and drawing editors. A shape is
 * a point (a pin), a polyline (a wall, open or `closed`) or a filled polygon
 * (a room). Tolerances are in world units — turn screen pixels into world
 * units with `screenToWorldDistance(px, pixelsPerUnit)`.
 */
import {
  boundsContainsPoint,
  distanceToSegment,
  pointDistance,
  pointInPolygon,
  segmentIntersectsBounds,
  type NxPlanBounds,
  type NxPlanPoint,
} from './geometry';

export type NxPlanShape =
  | { kind: 'point'; point: NxPlanPoint }
  | { kind: 'polyline'; points: readonly NxPlanPoint[]; closed?: boolean }
  | { kind: 'polygon'; points: readonly NxPlanPoint[] };

/**
 * `contain`: the shape lies completely inside the rectangle (a left-to-right
 * drag in CAD tools). `intersect`: it touches the rectangle anywhere (a
 * right-to-left „crossing“ drag).
 */
export type NxPlanMarqueeMode = 'contain' | 'intersect';

function edges(points: readonly NxPlanPoint[], closed: boolean): [NxPlanPoint, NxPlanPoint][] {
  const out: [NxPlanPoint, NxPlanPoint][] = [];
  for (let i = 0; i + 1 < points.length; i++) out.push([points[i]!, points[i + 1]!]);
  if (closed && points.length > 2) out.push([points[points.length - 1]!, points[0]!]);
  return out;
}

/** Shortest distance from `point` to a polyline (0 for a single point at it). */
export function distanceToPolyline(
  point: NxPlanPoint,
  points: readonly NxPlanPoint[],
  closed = false
): number {
  if (!points.length) return Infinity;
  if (points.length === 1) return pointDistance(point, points[0]!);
  let best = Infinity;
  for (const [a, b] of edges(points, closed)) best = Math.min(best, distanceToSegment(point, a, b));
  return best;
}

/**
 * How far `point` is from the shape, or `null` if it is further away than
 * `tolerance`. Inside a polygon counts as distance 0.
 */
export function hitTestShape(
  shape: NxPlanShape,
  point: NxPlanPoint,
  tolerance: number
): number | null {
  let distance: number;
  switch (shape.kind) {
    case 'point':
      distance = pointDistance(point, shape.point);
      break;
    case 'polyline':
      distance = distanceToPolyline(point, shape.points, shape.closed ?? false);
      break;
    case 'polygon':
      distance = pointInPolygon(point, shape.points)
        ? 0
        : distanceToPolyline(point, shape.points, true);
      break;
  }
  return distance <= tolerance ? distance : null;
}

/** Whether `point` is within `tolerance` of the segment `a`–`b`. */
export function hitTestSegment(
  point: NxPlanPoint,
  a: NxPlanPoint,
  b: NxPlanPoint,
  tolerance: number
): boolean {
  return distanceToSegment(point, a, b) <= tolerance;
}

/** Whether `point` is inside the polygon or within `tolerance` of its outline. */
export function hitTestPolygon(
  point: NxPlanPoint,
  polygon: readonly NxPlanPoint[],
  tolerance = 0
): boolean {
  return hitTestShape({ kind: 'polygon', points: polygon }, point, tolerance) !== null;
}

/**
 * All items hit at `point`, topmost first. Items later in the list are drawn
 * on top (SVG order), so they come first — a pin over a wall over a room.
 */
export function pickAllAt<T>(
  items: readonly T[],
  point: NxPlanPoint,
  tolerance: number,
  shapeOf: (item: T) => NxPlanShape | null | undefined
): T[] {
  const out: T[] = [];
  for (let i = items.length - 1; i >= 0; i--) {
    const shape = shapeOf(items[i]!);
    if (shape && hitTestShape(shape, point, tolerance) !== null) out.push(items[i]!);
  }
  return out;
}

/** The topmost item hit at `point`, or `null`. */
export function pickAt<T>(
  items: readonly T[],
  point: NxPlanPoint,
  tolerance: number,
  shapeOf: (item: T) => NxPlanShape | null | undefined
): T | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const shape = shapeOf(items[i]!);
    if (shape && hitTestShape(shape, point, tolerance) !== null) return items[i]!;
  }
  return null;
}

function shapePoints(shape: NxPlanShape): readonly NxPlanPoint[] {
  return shape.kind === 'point' ? [shape.point] : shape.points;
}

/** Whether a shape is selected by the marquee rectangle `rect` in `mode`. */
export function shapeInBounds(
  shape: NxPlanShape,
  rect: NxPlanBounds,
  mode: NxPlanMarqueeMode = 'contain'
): boolean {
  const points = shapePoints(shape);
  if (!points.length) return false;
  if (mode === 'contain') return points.every((p) => boundsContainsPoint(rect, p));
  if (points.some((p) => boundsContainsPoint(rect, p))) return true;
  if (shape.kind === 'point') return false;
  const closed = shape.kind === 'polygon' || (shape.closed ?? false);
  if (edges(points, closed).some(([a, b]) => segmentIntersectsBounds(a, b, rect))) return true;
  // A marquee drawn completely inside a room still touches it.
  return shape.kind === 'polygon' && pointInPolygon({ x: rect.minX, y: rect.minY }, points);
}

/** The items the marquee rectangle selects, in list order. */
export function selectInBounds<T>(
  items: readonly T[],
  rect: NxPlanBounds,
  mode: NxPlanMarqueeMode,
  shapeOf: (item: T) => NxPlanShape | null | undefined
): T[] {
  return items.filter((item) => {
    const shape = shapeOf(item);
    return !!shape && shapeInBounds(shape, rect, mode);
  });
}
